import { useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { motion } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import { RouteLoadingFallback } from '@/app/route-loading-fallback';
import bestHumanPortalLogoDark from '@/assets/branding/besthuman-client-portal-logo-dark.png';
import bestHumanPortalLogoLight from '@/assets/branding/besthuman-client-portal-logo-light.png';
import { useAppearVariants } from '@/shared/animations';
import { Button } from '@/shared/ui/Button';
import styles from './Home.module.css';

export function Home() {
  const { loginWithRedirect, isLoading, isAuthenticated } = useAuth0();
  const [searchParams] = useSearchParams();
  const { single: appearSingle } = useAppearVariants();
  const isSignupRedirect = searchParams.get('signup') === 'true';

  // WHY: Approval emails link here with `?signup=true` so new users land on
  // Auth0's signup screen instead of the default login tab.
  useEffect(() => {
    if (isSignupRedirect && !isAuthenticated && !isLoading) {
      loginWithRedirect({ authorizationParams: { screen_hint: 'signup' } });
    }
  }, [isSignupRedirect, isAuthenticated, isLoading, loginWithRedirect]);

  // WHY: Skip rendering the welcome UI entirely when a signup redirect is
  // pending. The effect above fires immediately after this paint, so showing
  // the "Welcome back" screen for one frame would be jarring.
  // By the time Home renders, isLoading is guaranteed false (App only mounts
  // AppRouter once isReady=true), so the effect will fire on the next tick.
  if (isSignupRedirect) {
    return <RouteLoadingFallback />;
  }

  return (
    <main className={styles.page}>
      <div className={styles.shell}>
        <motion.section className={styles.panelCard} aria-labelledby="home-title" {...appearSingle}>
          <div className={styles.logoStack}>
            <img src={bestHumanPortalLogoLight} alt="BestHuman Client Portal" className={styles.logoLight} />
            <img src={bestHumanPortalLogoDark} alt="BestHuman Client Portal" className={styles.logoDark} />
          </div>
          <h1 id="home-title" className={styles.title}>
            Welcome back
          </h1>
          <p className={styles.panelText}>Sign in after your BestHuman Portal access has been set up.</p>

          <div className={styles.actions}>
            <Button onClick={() => loginWithRedirect()} disabled={isLoading}>
              {isLoading ? 'Signing in...' : 'Sign In'}
            </Button>
          </div>

          <div className={styles.guidance} aria-labelledby="home-first-time-title">
            <h2 id="home-first-time-title" className={styles.guidanceTitle}>
              First time here?
            </h2>
            <p className={styles.guidanceText}>
              Use the invitation link from your email to complete onboarding. We'll send setup instructions when your
              portal access is ready.
            </p>
          </div>
        </motion.section>
      </div>
    </main>
  );
}
